import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { IChannelsStore } from 'shared/entities/store/channelsStore';
import { FieldArrayModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';

type PrivateFields = '_channelId';

type ChannelScenariosStoreProps = {
  channelsStore: IChannelsStore;
  channelId: string | null;
};

export class ChannelScenariosStore {
  readonly scenarioIdsDraft: FieldArrayModel<string> =
    new FieldArrayModel<string>([]);
  readonly savingStage: LoadingStageModel = new LoadingStageModel();
  private _channelId: string | null = null;
  private readonly channelsStore: IChannelsStore;

  constructor({ channelsStore, channelId }: ChannelScenariosStoreProps) {
    this.channelsStore = channelsStore;

    makeObservable<ChannelScenariosStore, PrivateFields>(this, {
      _channelId: observable,

      hasChanges: computed,
      editedChannelName: computed,

      setChannel: action.bound,
      toggleScenario: action.bound,
      save: action.bound
    });

    this.setChannel(channelId);
  }

  get hasChanges(): boolean {
    return this._channelId !== null && this.scenarioIdsDraft.touched;
  }

  get editedChannelName(): string | null {
    if (!this._channelId) {
      return null;
    }

    return this.channelsStore.channels.getEntity(this._channelId)?.name ?? null;
  }

  getScenarioIds = (channelId: string): string[] => {
    if (this._channelId === channelId) {
      return this.scenarioIdsDraft.value;
    }

    const channel = this.channelsStore.channels.getEntity(channelId);

    if (!channel) {
      return [];
    }

    return channel.scenarioIds.value;
  };

  setChannel = (channelId: string | null): void => {
    if (this._channelId === channelId) {
      return;
    }

    this._channelId = channelId;
    this._resetDraft();

    if (channelId === null) {
      return;
    }

    const channel = this.channelsStore.channels.getEntity(channelId);

    if (!channel) {
      return;
    }

    this.scenarioIdsDraft.changeValue([...channel.scenarioIds.value]);
    this.scenarioIdsDraft.resetTouched();
  };

  toggleScenario = ({
    channelId,
    scenarioId
  }: {
    channelId: string;
    scenarioId: string;
  }): void => {
    if (this.savingStage.isLoading) {
      return;
    }

    if (this._channelId !== channelId) {
      return;
    }

    const scenarioIds = this.scenarioIdsDraft.value;

    this.scenarioIdsDraft.changeValue(
      scenarioIds.includes(scenarioId)
        ? scenarioIds.filter((id) => id !== scenarioId)
        : [...scenarioIds, scenarioId]
    );
  };

  save = async (): Promise<boolean> => {
    if (this.savingStage.isLoading) {
      return false;
    }

    if (!this.hasChanges) {
      return true;
    }

    const channelId = this._channelId;
    const channel =
      channelId && this.channelsStore.channels.getEntity(channelId);

    if (!channel) {
      return false;
    }

    this.savingStage.loading();

    const response = await channel.updateScenarioIds([
      ...this.scenarioIdsDraft.value
    ]);

    if (response.isError) {
      this.savingStage.error();
      return false;
    }

    runInAction(() => {
      this.scenarioIdsDraft.resetTouched();
      this.savingStage.success();
    });
    return true;
  };

  private _resetDraft(): void {
    this.scenarioIdsDraft.changeValue([]);
    this.scenarioIdsDraft.resetTouched();
  }
}
